/* eslint-disable no-param-reassign */
import type {
  Cell,
  Column,
  Row,
  RowData,
  Table,
  TableFeature,
  Updater,
} from '@tanstack/react-table'
import { functionalUpdate, makeStateUpdater } from '@tanstack/react-table'

import type { CellSelectionState, CellSelectionTableState } from '../DataTable.types'

export const CellSelectionFeature: TableFeature<unknown> = {
  getInitialState: (state): CellSelectionTableState => ({ cellSelection: {}, ...state }),

  getDefaultOptions: <TData extends RowData>(table: Table<TData>) => ({
    onCellSelectionChange: makeStateUpdater('cellSelection', table),
  }),

  createTable: <TData extends RowData>(table: Table<TData>) => {
    table.setCellSelection = updater => {
      const safeUpdater: Updater<CellSelectionState> = old => functionalUpdate(updater, old)

      return table.options.onCellSelectionChange?.(safeUpdater)
    }

    table.resetCellSelection = (defaultState?: boolean) =>
      table.setCellSelection(defaultState ? {} : table.initialState.cellSelection)

    table.toggleCellSelected = ({ cell, isSelected }) => {
      table.setCellSelection(previous => {
        const next: CellSelectionState = { ...previous }
        const isNextSelected = !!next[cell.id]

        if (isSelected === isNextSelected) return previous

        if (isSelected) {
          next[cell.id] = true
        } else {
          delete next[cell.id]
        }

        return next
      })
    }

    table.getSelectedCells = () => table.getState().cellSelection
    table.getIsCellSelected = cellId => !!table.getState().cellSelection[cellId]
  },

  createCell: <TData extends RowData>(
    cell: Cell<TData, unknown>,
    _column: Column<TData, unknown>,
    _row: Row<TData>,
    table: Table<TData>
  ) => {
    cell.getIsSelected = () => !!table.getState().cellSelection[cell.id]
    cell.toggleSelected = isSelected => table.toggleCellSelected({ cell, isSelected })
  },
}

export const useCellSelection = <TData extends RowData>(cell: Cell<TData, unknown>) => {
  const metaEnable = cell.column.columnDef.meta?.enableCellSelection
  const isSelectableEnabled =
    typeof metaEnable === 'function' ? metaEnable({ cell, row: cell.row }) : (metaEnable ?? true)

  const isSelectable = typeof cell.toggleSelected === 'function' && isSelectableEnabled
  const isSelected =
    typeof cell.getIsSelected === 'function' && isSelectableEnabled ? cell.getIsSelected() : false

  const handleCellClick = () => {
    if (!isSelectable) return

    cell.toggleSelected(!isSelected)
  }

  return { isSelectable, isSelected, handleCellClick }
}
